import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Menu, X } from "lucide-react";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";

export function LandingNavbar() {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { enabled: showPricing } = useFeatureFlag(
    "public-pricing",
    FEATURE_FLAGS["public-pricing"] ?? false,
  );

  const scrollToSection = useCallback(
    (sectionId: string) => {
      setMobileMenuOpen(false);
      if (location.pathname !== "/landing") {
        navigate(`/landing#${sectionId}`);
      } else {
        const el = document.getElementById(sectionId);
        el?.scrollIntoView({ behavior: "smooth" });
      }
    },
    [location.pathname, navigate],
  );

  useEffect(() => {
    // Handle hash on mount/navigation (e.g. coming from /auth#features)
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const navLinkClass =
    "text-white/70 hover:text-white transition-colors p-0 h-auto font-normal shrink-0";

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-brand-container bg-brand text-white shadow-sm">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
        <Link
          to="/landing"
          className="flex shrink-0 items-center gap-2 font-display text-xl font-bold text-secondary-fixed"
        >
          <ClipboardCheck className="h-7 w-7" />
          <span className="hidden sm:inline">CheckMaty</span>
        </Link>

        <nav className="hidden min-w-0 items-center gap-4 text-sm lg:flex xl:gap-8">
          <Button
            variant="link"
            onClick={() => scrollToSection("features")}
            className={navLinkClass}
          >
            {t("landing.nav.features")}
          </Button>
          <Button
            variant="link"
            onClick={() => scrollToSection("process")}
            className={navLinkClass}
          >
            {t("landing.nav.process")}
          </Button>
          <Button
            variant="link"
            onClick={() => scrollToSection("security")}
            className={navLinkClass}
          >
            {t("landing.nav.security")}
          </Button>
          {showPricing && (
            <Button
              variant="link"
              onClick={() => scrollToSection("pricing")}
              className={navLinkClass}
            >
              {t("landing.nav.pricing")}
            </Button>
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <LanguageToggle tone="onDark" />
          <ThemeToggle tone="onDark" />
          <Button
            variant="ghost"
            size="icon"
            className="-mr-2 text-white/80 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          {isLoggedIn ? (
            <Button variant="action" size="sm" className="hidden sm:inline-flex" asChild>
              <Link to="/">{t("landing.nav.dashboard")}</Link>
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hidden text-white/80 hover:bg-white/10 hover:text-white sm:inline-flex"
                asChild
              >
                <Link to="/auth">{t("landing.nav.login")}</Link>
              </Button>
              <Button variant="action" size="sm" className="hidden sm:inline-flex" asChild>
                <Link to="/auth">{t("landing.industrial.hero.launch")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-brand-container bg-brand text-white animate-in slide-in-from-top-2 duration-200 lg:hidden">
          <div className="container mx-auto flex flex-col gap-1 px-4 py-3">
            <Button
              variant="ghost"
              onClick={() => scrollToSection("features")}
              className="justify-start text-white/80 hover:bg-white/10 hover:text-white"
            >
              {t("landing.nav.features")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection("process")}
              className="justify-start text-white/80 hover:bg-white/10 hover:text-white"
            >
              {t("landing.nav.process")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection("security")}
              className="justify-start text-white/80 hover:bg-white/10 hover:text-white"
            >
              {t("landing.nav.security")}
            </Button>
            {showPricing && (
              <Button
                variant="ghost"
                onClick={() => scrollToSection("pricing")}
                className="justify-start text-white/80 hover:bg-white/10 hover:text-white"
              >
                {t("landing.nav.pricing")}
              </Button>
            )}
            <div className="mt-1 flex gap-2 border-t border-brand-container pt-2 sm:hidden">
              {isLoggedIn ? (
                <Button variant="action" size="sm" className="w-full" asChild>
                  <Link to="/">{t("landing.nav.dashboard")}</Link>
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-white/80 hover:bg-white/10 hover:text-white"
                    asChild
                  >
                    <Link to="/auth">{t("landing.nav.login")}</Link>
                  </Button>
                  <Button variant="action" size="sm" className="flex-1" asChild>
                    <Link to="/auth">{t("landing.industrial.hero.launch")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
